import React, { useEffect } from "react";

import {
	UnauthenticatedUserHeader,
	AuthenticatedUserHeader,
} from "./components/HeaderBar";
import SubHeader from "./components/SubHeader";
import Login from "./components/Login";
import {
	PublicSharePageStandalone,
	PublicSharePageStandaloneForAuthenticatedUser,
} from "./components/Share.js";
import ForgotPassword from "./components/ForgotPassword";
import ChangePassword from "./components/ChangePassword";
import MyProfile from "./components/MyProfile";
import UpdateExchangeUrl from "./components/UpdateExchangeUrl";
import Settings from "./components/Settings";
import DevInfo from "./components/DevInfo";
import {
	EntraAppInitiateConsent,
	EntraAppConsentFinalDestination,
} from "./components/EntraApplicationConsent";
import Homepage from "./components/Homepage";
import FirmDocs from "./components/FirmDocs";
import UnderSubHeader from "./components/UnderSubHeader";
import Launcher from "./components/Launcher";
import ActivateLauncher from "./components/ActivateLauncher";
import Search from "./components/Search";
import Admin from "./components/Admin/Admin.js";
import TenantDetail from "./components/Admin/TenantDetail.js";
import AdminDeveloperObjectInfo from "./components/Admin/AdminDeveloperObjectInfo.js";
import NewUser from "./components/NewUser";
import EditUser from "./components/EditUser";

import NewTenant from "./components/NewTenant";
import { ClioIntegrationRedirect } from "./components/ClioIntegration";
import IntegrationSettings from "./components/IntegrationSettings.js";
import VersionControlSettings from "./components/VersionControlSettings.js";
import SharePasswordSettings from "./components/SharePasswordSettings.js";
import { ExternalSharingManagementFullPage } from "./components/ExternalSharingManagement";
import { ExternalShareDetail } from "./components/ExternalShareDetail";
import DeveloperOverridesSettings from "./components/Developer/DeveloperOverridesSettings";
import lxwConstants from "./config/lexworkplace-constants";
import { setHtmlDocumentTitle } from "./utilities/utilities";

// Param validation rules (replaces v5 inline regex in route paths)
const NUMERIC = /^\d+$/;
const validEditionCodes = Object.keys(
	lxwConstants.lexWorkplaceEditions.options
);

import {
	ClientTagManager,
	MatterTagManager,
	MatterTypeManager,
	OfficeBranchManager,
	DocumentTagManager,
	DocumentTypeManager,
	DocumentStatusManager,
} from "./components/TagManager";

import EditSmartFileAssociations from "./components/SmartEmailFiling/EditSmartFileAssociations";

import EditDialog from "./components/EditDialog";
import GlobalSnackbar from "./components/GlobalSnackbar";
import GlobalScreenSize from "./components/GlobalScreenSize";

import { LeftSidebarWithRouter } from "./components/LeftSidebar";
import ClientDetail from "./components/ClientDetail";
import NewClient from "./components/NewClient";
import ClientList from "./components/ClientList";
import AllMattersList from "./components/AllMattersList";
import NoteEditor from "./components/NoteEditor";

import DocumentProperties from "./components/DocumentProperties";

import NewMatter from "./components/NewMatter";
import DefaultMatterPermissions from "./components/DefaultMatterPermissions";
import MatterDetail from "./components/MatterDetail";
import {
	checkCognitoAuthentication,
	logOut,
	setRedirectUrl,
	getMyUserDataFromDatabase,
} from "./actions/userAuthActions";
import { navigated } from "./actions/globalSearchbarActions";

import { getCustomColumns } from "./actions/customColumnActions";
import { setSubheaderText } from "./actions/navigateActions";

import PermissionsGroupList from "./components/PermissionsGroupList";
import FullScopeAiPage from "./components/FullScopeAi/FullScopeAiPage";
import MyFirm from "./components/MyFirm/MyFirm";

import { MantineProvider, AppShell, createTheme } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

const mantineTheme = createTheme({
	focusRing: "never",
	fontFamily: "Poppins, sans-serif",
	fontSizes: {
		xs: "12px",
		sm: "14px",
		md: "14px",
		lg: "16px",
		xl: "20px",
	},
	colors: {
		"lxw-blue": [
			"#f3f7ff", // 0 - main-20
			"#cddcfd", // 1 - main-100
			"#a7caed", // 2 - main-150
			"#92b3fb", // 3 - main-200
			"#5789f9", // 4 - main-300
			"#1c60f7", // 5 - main-400
			"#0a56fe", // 6 - main-500
			"#0745cf", // 7 - main-600
			"#053194", // 8 - main-800
			"#032066", // 9
		],
		"lw-red": [
			"#fff5f5",
			"#ffe3e3",
			"#ecc5c5",
			"#ffa8a8",
			"#ff8787",
			"#fd5351",
			"#d32f2f",
			"#c92a2a",
			"#a51111",
			"#8a0000",
		],
		"lw-green": [
			"#ebfbee",
			"#d3f9d8",
			"#b2f2bb",
			"#8ce99a",
			"#69db7c",
			"#3cad2a",
			"#2f9e1f",
			"#2b8a3e",
			"#237032",
			"#1b5727",
		],
		"lw-gold": [
			"#fffde6",
			"#fff9b1",
			"#fff176",
			"#ffee58",
			"#efcf00",
			"#f39c11",
			"#f57f17",
			"#e65100",
			"#bf360c",
			"#8a2600",
		],
	},
	primaryColor: "lxw-blue",
	components: {
		Button: {
			defaultProps: {
				variant: "filled",
			},
			styles: {
				root: {
					textTransform: "none",
				},
			},
		},
		ActionIcon: {
			defaultProps: {
				color: "black",
			},
		},
		Checkbox: {
			defaultProps: {
				color: "lxw-blue",
				size: "sm",
			},
		},
		Loader: {
			defaultProps: {
				color: "lxw-blue",
			},
		},
		Modal: {
			defaultProps: {
				centered: true,
			},
			styles: {
				title: {
					fontFamily: "Poppins, sans-serif",
					fontWeight: 600,
				},
			},
		},
		Switch: {
			defaultProps: {
				color: "lxw-blue",
				size: "sm",
			},
		},
		Tabs: {
			defaultProps: {
				color: "lxw-blue",
			},
		},
		Radio: {
			defaultProps: {
				color: "lxw-blue",
			},
		},
		Progress: {
			defaultProps: {
				color: "lxw-blue",
			},
		},
		Divider: {
			styles: {
				root: {
					borderColor: "var(--lw-medium-grey)",
				},
			},
		},
	},
});
import { connect, useDispatch } from "react-redux";

import {
	createBrowserRouter,
	RouterProvider,
	Routes,
	Route,
	Navigate,
	useLocation,
	useNavigate,
	useParams,
} from "react-router-dom";
import RouteElement from "./utilities/RouteElement";

import { Provider } from "react-redux";
import PropTypes from "prop-types";
import store from "./store";

import TextDisplayWithCopyToClipboard from "./components/TextDisplayWithCopyToClipboard";
import Loading from "./components/Loading/FullPageLoading";
import FullPageError from "./components/Loading/FullPageError";
import ErrorBoundaryPage from "./components/Loading/ErrorBoundaryPage";

import "./components/LabeledInput.css";
import "./components/SubHeader.css";

import "./App.css";
import "./globalColors.css";
import ReactDialog from "./components/reactDialog";
import { KeyboardEventProvider } from "./components/KeyboardEventContext";
import SuperAdmin from "./components/SuperAdmin.js";
import EditWhatsNewWrapper from "./components/EditWhatsNew/EditWhatsNewWrapper.js";
import ThemeExperiment from "./components/Developer/ThemeExperiment.js";
import DocumentCompare from "./components/DocumentCompare.js";
import DocumentStampSettingsWrapper from "./components/Settings/DocumentStampSettings.js";

import MandatoryProfilingSettingsWrapper from "./components/mandatoryProfiling/MandatoryProfilingSettings.js";
import TrashBinContainer from "./components/TrashBin/TrashBinContainer.js";
import { TelemetryReporter } from "./components/TelemetryReporter.js";
import { MfaSettingsPage } from "./components/MfaSettingsPage.js";
import { settingsRoutes } from "./config/routes.js";
import NewEditPermissionsGroupV2 from "./components/NewEditPermissionsGroupV2.js";

const UnconnectedLogout = ({ logOut }) => {
	React.useEffect(() => {
		logOut();
	}, [logOut]);
	return <Loading />;
};
const Logout = connect(null, { logOut })(UnconnectedLogout);

const testRoute = () => {
	return <div>Test</div>;
};

const EditMatter = (props) => <NewMatter {...props} editMode />;

const EditClient = (props) => <NewClient {...props} editMode />;

const EditTenant = (props) => <NewTenant {...props} editMode />;

const OutlookAddinAddress = () => (
	<div>
		<p>
			Copy the following URL to the &quot;get add-ins&quot; section of
			Microsoft Outlook. See the{" "}
			<a
				target="_blank"
				rel="noopener noreferrer"
				href="https://lexworkplace.com/setup-guide"
			>
				setup guide
			</a>{" "}
			for more detailed instructions.
		</p>
		<TextDisplayWithCopyToClipboard
			style={{ width: "800px" }}
			label="Outlook Addin URL:"
			value="https://addin.lexworkplace.app/manifest-dist.xml"
		/>
	</div>
);

const SearchHackWrapper = () => {
	const params = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(
			setSubheaderText({
				subheaderText: "Search LexWorkplace",
				subheaderIconName: "IconListSearch",
			})
		);
	}, [dispatch]);

	if (!NUMERIC.test(params.hack)) {
		return <Navigate to="/" replace />;
	}

	const history = {
		push: navigate,
		replace: (to) => navigate(to, { replace: true }),
		goBack: () => navigate(-1),
		location,
		length: window.history.length,
	};
	const match = { params };

	return (
		<Search
			match={match}
			history={history}
			location={location}
			key={"" + params.hack}
			hack={params.hack}
		/>
	);
};

const GetInitialUserDataUnconnected = ({
	getMyUserDataFromDatabase,
	getCustomColumns,
}) => {
	/* 
		this will run each time this component is mounted
		when getMyUserDataFromDatabase() successful response comes back,
		it will then set initialUserDataLoadedFromDB=true, which
		will allow the routes to render, with all necessary data about the
		authenticated user
	*/
	useEffect(() => {
		getMyUserDataFromDatabase();
		getCustomColumns();
	}, []);
	return null;
};

const GetInitialUserDataFromDB = connect(null, {
	getCustomColumns,
	getMyUserDataFromDatabase,
})(GetInitialUserDataUnconnected);

const LoginRequired = ({
	user,
	hasPermissionsAccess,
	initialUserDataLoadedFromDB,
	userRights,
}) => {
	const cognitoUser = user.cognitoUser;
	const cognitoGroups = cognitoUser.cognitoGroups;

	const isLexworkplaceAdmin =
		cognitoGroups && cognitoGroups.includes("LexWorkplace_Admin");

	const isSuperAdmin =
		cognitoGroups && cognitoGroups.includes("LexWorkplace_Super_Admin");

	if (!initialUserDataLoadedFromDB) {
		return <Loading />;
	}

	return (
		<>
			<EditDialog />
			<ReactDialog />
			<GlobalSnackbar />
			<GlobalScreenSize />
			<SubHeader />
			<UnderSubHeader>
				<Routes>
					<Route
						path="/"
						element={
							<RouteElement
								component={Homepage}
								subheaderText="Welcome to LexWorkplace"
							/>
						}
					/>
					<Route
						path="/launcher"
						element={
							<RouteElement
								component={Launcher}
								subheaderText="Connect LexWorkplace Launcher"
							/>
						}
					/>
					<Route
						path="/activateLauncher"
						element={
							<RouteElement
								component={ActivateLauncher}
								subheaderText="Launcher Successfully Activated"
							/>
						}
					/>
					<Route
						path="/matters"
						element={
							<RouteElement
								component={AllMattersList}
								subheaderText="Matters"
								subheaderIconName="IconBriefcase"
							/>
						}
					/>
					<Route
						path="/docs/:fullpath"
						element={
							<RouteElement
								component={DocumentProperties}
								subheaderText="Document Properties"
								paramValidation={{ fullpath: NUMERIC }}
							/>
						}
					/>
					<Route
						path="/notes/:fullpath"
						element={
							<RouteElement
								// shares a key with the create route so a new note can
								// switch to this route without remounting; NoteEditor
								// decides when to remount itself (see NoteEditorRoute)
								key="noteEditor"
								component={NoteEditor}
								paramValidation={{ fullpath: NUMERIC }}
							/>
						}
					/>
					<Route
						path="/notes/new/:matterID/:directoryID/*"
						element={
							<RouteElement
								key="noteEditor"
								component={NoteEditor}
								createMode
								splatParamName="fullpath"
								paramValidation={{
									matterID: NUMERIC,
									directoryID: NUMERIC,
								}}
							/>
						}
					/>

					<Route
						path="/firmDocs/:namespace/archive"
						element={<Navigate to="docs" relative="path" replace />}
					/>
					<Route
						path="/firmDocs/:namespace/archive/:context"
						element={
							<RouteElement
								component={FirmDocs}
								staticParams={{ root: "archive" }}
								paramValidation={{
									namespace: ["public", "private"],
									context: ["docs"],
								}}
							/>
						}
					/>
					<Route
						path="/firmDocs/:namespace/trash/:context"
						element={
							<RouteElement
								component={FirmDocs}
								staticParams={{ root: "trash" }}
								paramValidation={{
									namespace: ["public", "private"],
									context: ["docs"],
								}}
							/>
						}
					/>
					<Route
						path="/firmDocs/:namespace/trash"
						element={<Navigate to="docs" relative="path" replace />}
					/>
					<Route
						path="/firmDocs/:namespace/docs/*"
						element={
							<RouteElement
								component={FirmDocs}
								splatParamName="fullpath"
								paramValidation={{
									namespace: ["public", "private"],
								}}
							/>
						}
					/>
					<Route
						path="/firmDocs/:namespace/docs"
						element={
							<RouteElement
								component={FirmDocs}
								paramValidation={{
									namespace: ["public", "private"],
								}}
							/>
						}
					/>
					<Route
						path="/firmDocs/:namespace"
						element={<Navigate to="docs" relative="path" replace />}
					/>

					<Route
						path="/clients"
						element={
							<RouteElement
								component={ClientList}
								subheaderText="Clients"
								subheaderIconName="IconUsersGroup"
							/>
						}
					/>
					<Route
						path="/clients/:id"
						element={
							<RouteElement
								component={ClientDetail}
								paramValidation={{ id: NUMERIC }}
							/>
						}
					/>
					<Route
						path="/clients/:id/edit"
						element={
							<RouteElement
								component={EditClient}
								editMode
								paramValidation={{ id: NUMERIC }}
							/>
						}
					/>
					<Route
						path="/clients/new"
						element={
							<RouteElement
								component={NewClient}
								subheaderText="New Client"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_DOCUMENT_TAGS.href}
						element={
							<RouteElement
								component={DocumentTagManager}
								subheaderText={
									settingsRoutes.MANAGE_DOCUMENT_TAGS.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_DOCUMENT_TYPES.href}
						element={
							<RouteElement
								component={DocumentTypeManager}
								subheaderText={
									settingsRoutes.MANAGE_DOCUMENT_TYPES.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_DOCUMENT_STATUSES.href}
						element={
							<RouteElement
								component={DocumentStatusManager}
								subheaderText={
									settingsRoutes.MANAGE_DOCUMENT_STATUSES
										.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_CLIENT_TAGS.href}
						element={
							<RouteElement
								component={ClientTagManager}
								subheaderText={
									settingsRoutes.MANAGE_CLIENT_TAGS.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_MATTER_TAGS.href}
						element={
							<RouteElement
								component={MatterTagManager}
								subheaderText={
									settingsRoutes.MANAGE_MATTER_TAGS.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_MATTER_TYPES.href}
						element={
							<RouteElement
								component={MatterTypeManager}
								subheaderText={
									settingsRoutes.MANAGE_MATTER_TYPES.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANAGE_OFFICE_BRANCH.href}
						element={
							<RouteElement
								component={OfficeBranchManager}
								subheaderText={
									settingsRoutes.MANAGE_OFFICE_BRANCH.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					<Route
						path={
							settingsRoutes.MANAGE_SMART_FILE_ASSOCIATIONS.href
						}
						element={
							<RouteElement
								component={EditSmartFileAssociations}
								subheaderText={
									settingsRoutes
										.MANAGE_SMART_FILE_ASSOCIATIONS.label
								}
								subheaderIconName="IconTag"
							/>
						}
					/>
					{/* do not add this to settingsRoutes */}
					<Route
						path="/settings/developer"
						element={
							<RouteElement
								component={DeveloperOverridesSettings}
								subheaderText="Developer Settings"
							/>
						}
					/>

					<Route
						path="/matters/new"
						element={
							<RouteElement
								component={NewMatter}
								subheaderText="New Matter"
							/>
						}
					/>
					<Route
						path="/matters/new/:clientID"
						element={
							<RouteElement
								component={NewMatter}
								subheaderText="New Matter"
								paramValidation={{ clientID: NUMERIC }}
							/>
						}
					/>
					<Route
						path="/matters/:id/edit"
						element={
							<RouteElement
								component={EditMatter}
								paramValidation={{ id: NUMERIC }}
							/>
						}
					/>

					{userRights.UPDATE_MATTER_PERMISSIONS && (
						<Route
							path="/matters/:id/edit/permissions"
							element={
								<RouteElement
									component={EditMatter}
									permissions
									paramValidation={{ id: NUMERIC }}
								/>
							}
						/>
					)}
					{userRights.UPDATE_MATTER_PERMISSIONS && (
						<Route
							path="/matters/new/permissions"
							element={
								<RouteElement
									component={NewMatter}
									permissions
								/>
							}
						/>
					)}

					{userRights.MANAGE_DEFAULT_MATTER_PERMISSIONS && (
						<Route
							path={
								settingsRoutes.EDIT_DEFAULT_MATTER_PERMISSIONS
									.href
							}
							element={
								<RouteElement
									component={DefaultMatterPermissions}
									subheaderText={
										settingsRoutes
											.EDIT_DEFAULT_MATTER_PERMISSIONS
											.label
									}
								/>
							}
						/>
					)}

					{hasPermissionsAccess && (
						<Route
							path="/permissions/groups/:root"
							element={
								<RouteElement
									component={PermissionsGroupList}
									subheaderText="Manage Groups and Rights"
									paramValidation={{
										root: ["list", "calculate"],
									}}
								/>
							}
						/>
					)}
					{hasPermissionsAccess && (
						<Route
							path="/permissions/groups/create"
							element={
								<RouteElement
									component={NewEditPermissionsGroupV2}
									createNew
									subheaderText="New Permission Group"
								/>
							}
						/>
					)}
					{hasPermissionsAccess && (
						<Route
							path="/permissions/groups/edit/:groupID"
							element={
								<RouteElement
									component={NewEditPermissionsGroupV2}
									editMode
									subheaderText="Edit Permission Group"
									paramValidation={{ groupID: NUMERIC }}
								/>
							}
						/>
					)}

					<Route
						path="/matters/:id/archive/:context"
						element={
							<RouteElement
								component={MatterDetail}
								staticParams={{ root: "archive" }}
								paramValidation={{
									id: NUMERIC,
									context: ["docs", "email", "notes"],
								}}
							/>
						}
					/>
					<Route
						path="/matters/:id/trash/:context"
						element={
							<RouteElement
								component={MatterDetail}
								staticParams={{ root: "trash" }}
								paramValidation={{
									id: NUMERIC,
									context: ["docs", "email", "notes"],
								}}
							/>
						}
					/>
					<Route
						path="/matters/:id/archive"
						element={<Navigate to="docs" relative="path" replace />}
					/>
					<Route
						path="/matters/:id/trash"
						element={<Navigate to="docs" relative="path" replace />}
					/>
					<Route
						path="/matters/:id/:root/*"
						element={
							<RouteElement
								component={MatterDetail}
								splatParamName="fullpath"
								paramValidation={{
									id: NUMERIC,
									root: [
										"docs",
										"email",
										"notes",
										"sharing",
										"ai",
									],
								}}
							/>
						}
					/>
					<Route
						path="/matters/:id/:root"
						element={
							<RouteElement
								component={MatterDetail}
								paramValidation={{
									id: NUMERIC,
									root: [
										"docs",
										"email",
										"notes",
										"sharing",
										"ai",
									],
								}}
							/>
						}
					/>

					<Route
						path="/settings"
						element={
							<RouteElement
								component={Settings}
								subheaderText="LexWorkplace Settings"
								subheaderIconName="IconSettings"
							/>
						}
					/>
					<Route
						path={settingsRoutes.CHANGE_PASSWORD.href}
						element={
							<RouteElement
								component={ChangePassword}
								subheaderText={
									settingsRoutes.CHANGE_PASSWORD.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.MY_PROFILE.href}
						element={
							<RouteElement
								component={MyProfile}
								subheaderText={settingsRoutes.MY_PROFILE.label}
							/>
						}
					/>
					<Route
						path={settingsRoutes.UPDATE_EXCHANGE_URL.href}
						element={
							<RouteElement
								component={UpdateExchangeUrl}
								subheaderText={
									settingsRoutes.UPDATE_EXCHANGE_URL.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.GET_OUTLOOK_ADDIN_ADDRESS.href}
						element={
							<RouteElement
								component={OutlookAddinAddress}
								subheaderText={
									settingsRoutes.GET_OUTLOOK_ADDIN_ADDRESS
										.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.CONFIGURE_MFA.href}
						element={
							<RouteElement
								component={MfaSettingsPage}
								subheaderText={
									settingsRoutes.CONFIGURE_MFA.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.INTEGRATIONS.href}
						element={
							<RouteElement
								component={IntegrationSettings}
								subheaderText={
									settingsRoutes.INTEGRATIONS.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.SHARE_PASSWORD_SETTINGS.href}
						element={
							<RouteElement
								component={SharePasswordSettings}
								subheaderText={
									settingsRoutes.SHARE_PASSWORD_SETTINGS.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.VERSION_CONTROL.href}
						element={
							<RouteElement
								component={VersionControlSettings}
								subheaderText={
									settingsRoutes.VERSION_CONTROL.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.DOCUMENT_STAMPING.href}
						element={
							<RouteElement
								component={DocumentStampSettingsWrapper}
								subheaderText={
									settingsRoutes.DOCUMENT_STAMPING.label
								}
							/>
						}
					/>
					<Route
						path={settingsRoutes.MANDATORY_PROFILING.href}
						element={
							<RouteElement
								component={MandatoryProfilingSettingsWrapper}
								subheaderText={
									settingsRoutes.MANDATORY_PROFILING.label
								}
							/>
						}
					/>
					<Route
						path="/search"
						element={
							<RouteElement
								component={Search}
								subheaderText="Search LexWorkplace"
								subheaderIconName="IconListSearch"
							/>
						}
					/>
					<Route
						path="/search/:hack"
						element={<SearchHackWrapper />}
					/>

					<Route
						path="/about"
						element={
							<RouteElement
								component={DevInfo}
								subheaderText="Developer Info"
							/>
						}
					/>
					<Route
						path="/logout"
						element={<RouteElement component={Logout} />}
					/>
					<Route
						path="/test"
						element={<RouteElement component={testRoute} />}
					/>
					<Route
						path="/integrations/clio/authorize"
						element={
							<RouteElement component={ClioIntegrationRedirect} />
						}
					/>

					<Route
						path="/sharing/outgoing"
						element={
							<RouteElement
								component={ExternalSharingManagementFullPage}
								subheaderText="Manage Document Sharing"
								subheaderIconName="IconShare2"
							/>
						}
					/>
					<Route
						path="/sharing/outgoing/:shareID"
						element={
							<RouteElement
								component={ExternalShareDetail}
								subheaderText="Share Detail"
								paramValidation={{ shareID: NUMERIC }}
							/>
						}
					/>
					{/* all new share routes are created with US|CA in the url. This is for any legacy routes that don't have it */}
					<Route
						path="/share/download/:accessHash"
						element={
							<RouteElement
								component={
									PublicSharePageStandaloneForAuthenticatedUser
								}
								subheaderText="Share Detail"
							/>
						}
					/>
					<Route
						path="/share/:userRegion/download/:accessHash"
						element={
							<RouteElement
								component={
									PublicSharePageStandaloneForAuthenticatedUser
								}
								subheaderText="Share Detail"
								paramValidation={{
									userRegion: ["US", "CA"],
								}}
							/>
						}
					/>

					<Route
						path="/ai"
						element={
							<RouteElement
								component={FullScopeAiPage}
								subheaderText="LexWorkplace AI"
							/>
						}
					/>
					<Route
						path="/ai/:sessionId"
						element={
							<RouteElement
								component={FullScopeAiPage}
								subheaderText="LexWorkplace AI"
							/>
						}
					/>
					<Route
						path="/myFirm"
						element={
							<RouteElement
								component={MyFirm}
								subheaderText="My Firm"
								subheaderIconName="IconBuilding"
							/>
						}
					/>
					<Route
						path="/document/compare"
						element={
							<RouteElement
								component={DocumentCompare}
								subheaderText="Document Compare"
							/>
						}
					/>
					<Route
						path="/configureEntraApplication"
						element={
							<RouteElement
								component={EntraAppInitiateConsent}
								subheaderText="Add LexWorkplace for Outlook"
							/>
						}
					/>
					<Route
						path="/addedEntraAppSuccess"
						element={
							<RouteElement
								component={EntraAppConsentFinalDestination}
							/>
						}
					/>

					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/docs/:documentID"
							element={
								<RouteElement
									component={AdminDeveloperObjectInfo}
									paramValidation={{
										tenantID: NUMERIC,
										documentID: NUMERIC,
									}}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/email/:emailID"
							element={
								<RouteElement
									component={AdminDeveloperObjectInfo}
									paramValidation={{
										tenantID: NUMERIC,
										emailID: NUMERIC,
									}}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/notes/:matterNoteID"
							element={
								<RouteElement
									component={AdminDeveloperObjectInfo}
									paramValidation={{
										tenantID: NUMERIC,
										matterNoteID: NUMERIC,
									}}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/:tenantSubpage"
							element={
								<RouteElement
									component={TenantDetail}
									paramValidation={{
										tenantID: NUMERIC,
										tenantSubpage: [
											"users",
											"searchindex",
											"batchjobs",
											"records",
										],
									}}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin"
							element={
								<RouteElement
									component={Admin}
									subheaderText="LexWorkplace Firms"
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/user/new"
							element={
								<RouteElement
									component={NewUser}
									paramValidation={{ tenantID: NUMERIC }}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/:tenantID/user/edit/:sub"
							element={
								<RouteElement
									component={EditUser}
									paramValidation={{
										tenantID: NUMERIC,
										sub: /^[0-9a-fA-F-]+$/,
									}}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/edit/:tenantID"
							element={
								<RouteElement
									component={EditTenant}
									paramValidation={{ tenantID: NUMERIC }}
								/>
							}
						/>
					)}
					{isLexworkplaceAdmin && (
						<Route
							path="/admin/tenant/new/:newFirmEditionCode"
							element={
								<RouteElement
									component={NewTenant}
									subheaderText="New Firm"
									paramValidation={{
										newFirmEditionCode: validEditionCodes,
									}}
								/>
							}
						/>
					)}

					{/* super admin routes */}
					{isSuperAdmin && (
						<Route
							path="/superAdmin"
							element={
								<RouteElement
									component={SuperAdmin}
									subheaderText="Super Admin Settings"
								/>
							}
						/>
					)}
					{isSuperAdmin && (
						<Route
							path="/superAdmin/editWhatsNewDropdown"
							element={
								<RouteElement
									component={EditWhatsNewWrapper}
									subheaderText="Edit What's New Dropdown Items"
								/>
							}
						/>
					)}
					{isSuperAdmin && (
						<Route
							path="/developer/themeExperiment"
							element={
								<RouteElement
									component={ThemeExperiment}
									subheaderText="Theme Experiment"
								/>
							}
						/>
					)}

					{/* if this route is changed be sure to update the look for trashPageContext in documentActions.js:: fn() determineTrashPageContext */}
					{userRights.CAN_PERMANENT_DELETE && (
						<Route
							path="/trash/:trashPageContext"
							element={
								<RouteElement
									component={TrashBinContainer}
									subheaderText="Trash Bin"
									subheaderIconName="IconTrash"
									paramValidation={{
										trashPageContext: [
											"docs",
											"email",
											"notes",
											"matters",
											"clients",
											"activity",
										],
									}}
								/>
							}
						/>
					)}

					<Route
						path="*"
						element={
							<RouteElement component={AuthenticatedCatchAll} />
						}
					/>
				</Routes>
			</UnderSubHeader>
		</>
	);
};

class UnconnectedUnauthenticatedRedirect extends React.Component {
	componentDidMount() {
		const path = this.props.location.pathname;
		if (path !== "/logout") {
			this.props.setRedirectUrl(path);
		}
	}
	render() {
		return <Navigate to="/login" replace />;
	}
}
const UnauthenticatedRedirect = connect(null, { setRedirectUrl })(
	UnconnectedUnauthenticatedRedirect
);

class UnconnectedAuthenticatedRedirect extends React.Component {
	componentDidMount() {
		this.props.setRedirectUrl("/");
	}
	render() {
		return <Navigate to={this.props.redirectLocation} replace />;
	}
}
const AuthenticatedRedirect = connect(
	(state) => ({ redirectLocation: state.user.redirectLocation }),
	{ setRedirectUrl }
)(UnconnectedAuthenticatedRedirect);

/*
	Smart catch-all for authenticated routes:
	If there's a pending redirect (from a pre-login visit), redirect there.
	Otherwise show the error page so users know the route doesn't exist.
*/
const UnconnectedAuthenticatedCatchAll = (props) => {
	if (props.redirectLocation && props.redirectLocation !== "/") {
		return <AuthenticatedRedirect {...props} />;
	}
	return <Navigate to="/" replace />;
};
const AuthenticatedCatchAll = connect(
	(state) => ({ redirectLocation: state.user.redirectLocation }),
	{ setRedirectUrl }
)(UnconnectedAuthenticatedCatchAll);

const appShellMainStyles = {
	main: {
		display: "flex",
		flexDirection: "column",
		height: "100vh",
		backgroundColor: "white",
		color: "var(--lxw-main-text)",
	},
	navbar: {
		borderRight: "none",
	},
};

const AppShellLayout = (props) => {
	const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
		useDisclosure();

	if (props.determiningAuthStatus) {
		return <Loading />;
	}

	if (!props.isLoggedIn) {
		return (
			<AppShell
				header={{ height: 76 }}
				padding={0}
				styles={appShellMainStyles}
			>
				<AppShell.Header>
					<UnauthenticatedUserHeader />
				</AppShell.Header>
				<AppShell.Main>
					<GlobalSnackbar />
					<Routes>
						<Route
							path="/login"
							element={<RouteElement component={Login} />}
						/>
						<Route
							path="/forgotPassword"
							element={
								<RouteElement component={ForgotPassword} />
							}
						/>
						<Route
							path="/changePassword"
							element={
								<RouteElement component={ChangePassword} />
							}
						/>
						<Route
							path="/about"
							element={<RouteElement component={DevInfo} />}
						/>
						<Route
							path="/addedEntraAppSuccess"
							element={
								<RouteElement
									component={EntraAppConsentFinalDestination}
								/>
							}
						/>
						<Route
							path="/configureEntraApplication"
							element={
								<RouteElement
									component={EntraAppInitiateConsent}
									subheaderText="Add LexWorkplace for Outlook"
								/>
							}
						/>
						<Route
							path="/share/:userRegion/download/:accessHash"
							element={
								<RouteElement
									component={PublicSharePageStandalone}
									paramValidation={{
										userRegion: ["US", "CA"],
									}}
								/>
							}
						/>
						{/* all new share routes are created with US|CA in the url. This is for any legacy routes that don't have it */}
						<Route
							path="/share/download/:accessHash"
							element={
								<RouteElement
									component={PublicSharePageStandalone}
								/>
							}
						/>
						<Route
							path="*"
							element={
								<RouteElement
									component={UnauthenticatedRedirect}
								/>
							}
						/>
					</Routes>
				</AppShell.Main>
			</AppShell>
		);
	}

	// authenticated layout
	const cognitoGroups = props.user?.cognitoUser?.cognitoGroups;
	const isLexworkplaceAdmin =
		cognitoGroups && cognitoGroups.includes("LexWorkplace_Admin");
	const isSuperAdmin =
		cognitoGroups && cognitoGroups.includes("LexWorkplace_Super_Admin");

	if (props.userDataLoadError) {
		return <FullPageError logOut={props.logOut} />;
	}

	return (
		<div
			className={
				(props.allowUnderHeaderOverflow
					? "allowUnderHeaderOverflow"
					: "") +
				(props.forcePaginationVisible ? " forcePaginationVisible" : "")
			}
		>
			<GetInitialUserDataFromDB />
			<TelemetryReporter />
			<AppShell
				header={{ height: 76 }}
				navbar={{
					width: props.sidenavExpanded ? 240 : 64,
					breakpoint: "sm",
					collapsed: { mobile: !mobileOpened },
				}}
				padding={0}
				styles={appShellMainStyles}
			>
				<AppShell.Header>
					<AuthenticatedUserHeader mobileToggle={toggleMobile} />
				</AppShell.Header>
				<AppShell.Navbar>
					<LeftSidebarWithRouter
						isLexworkplaceAdmin={isLexworkplaceAdmin}
						isSuperAdmin={isSuperAdmin}
						closeMobileSidebar={closeMobile}
					/>
				</AppShell.Navbar>
				<AppShell.Main>
					<LoginRequired
						user={props.user}
						hasPermissionsAccess={props.hasPermissionsAccess}
						initialUserDataLoadedFromDB={
							props.initialUserDataLoadedFromDB
						}
						userDataLoadError={props.userDataLoadError}
						logOut={props.logOut}
						userRights={props.userRights}
					/>
				</AppShell.Main>
			</AppShell>
		</div>
	);
};

function AppContainer(props) {
	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		window.globalHistory = {
			push: navigate,
			replace: (to) => navigate(to, { replace: true }),
			goBack: () => navigate(-1),
		};

		/*
			When opening a new lxw link (eg: lexworkplace.app/docs/33139 to
			a document properties page) using the history object to goBack will
			return the user to their browsers default location of a new window.
			In LexWorkplace, we use the "cancel" button on many pages to bring the user
			back to their previous location, and we want to make sure it's a reasonable
			LexWorkplace page.

			We currently need to call this "safe" goBack function
			from an action file, which is the reason for using the global `window` object.
		*/
		window.safeGoBack = (locationIfNoHistory = "/") => {
			if (window.history.length <= 2) {
				navigate(locationIfNoHistory, { replace: true });
			} else {
				navigate(-1);
			}
		};

		props.checkCognitoAuthentication();
		setHtmlDocumentTitle();
	}, []);

	useEffect(() => {
		props.navigated(location.pathname);
	}, [location.pathname]);

	return <AppShellLayout {...props} />;
}
AppContainer.propTypes = {
	loading: PropTypes.bool.isRequired,
	isLoggedIn: PropTypes.bool.isRequired,
	checkCognitoAuthentication: PropTypes.func.isRequired,
	navigated: PropTypes.func.isRequired,
};
const appContainerMapStateToProps = (state) => ({
	initialUserDataLoadedFromDB: state.user.initialUserDataLoadedFromDB,
	loading: state.user.loading,
	redirectLocation: state.user.redirectLocation,
	isLoggedIn: state.user.isLoggedIn,
	determiningAuthStatus: state.user.determiningAuthStatus,
	allowUnderHeaderOverflow: state.global.allowUnderHeaderOverflow,
	forcePaginationVisible: state.global.forcePaginationVisible,
	sidenavExpanded: state.browserLocalstorage.sidenavExpanded,
	user: state.user,
	hasPermissionsAccess: state.user.hasPermissionsAccess,
	userDataLoadError: state.user.userDataLoadError,
	userRights: state.user.userRights,
});
const ConnectedAppContainer = connect(appContainerMapStateToProps, {
	checkCognitoAuthentication,
	setRedirectUrl,
	getCustomColumns,
	navigated,
	logOut,
})(AppContainer);

const router = createBrowserRouter([
	{
		path: "*",
		element: <ConnectedAppContainer />,
		errorElement: <ErrorBoundaryPage />,
	},
]);

const App = () => {
	return (
		<MantineProvider theme={mantineTheme}>
			<Provider store={store}>
				<KeyboardEventProvider>
					<RouterProvider router={router} />
				</KeyboardEventProvider>
			</Provider>
		</MantineProvider>
	);
};

export default App;
